/**
 * Report Service — fetches interactive Report Documents and individual claims.
 */
import { API_CONFIG } from '../config/constants';
import type { ReportDocument, HtmlReportDocument, Claim } from '../types/report';
import { downloadBlob, filenameFromContentDisposition } from '../utils/downloadBlob';

const API_URL = API_CONFIG.BASE_URL;

/** A row in the reports list (owner-scoped, newest first). */
export interface ReportListItem {
  id: number;
  title: string;
  genre: string;
  created_at: string;
  updated_at: string;
}

export async function listReports(): Promise<ReportListItem[]> {
  const response = await fetch(`${API_URL}/api/reports`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Failed to load reports: ${response.status}`);
  }
  return response.json();
}

export async function getReport(
  id: string,
  filter?: string,
): Promise<ReportDocument | HtmlReportDocument> {
  const qs = filter ? `?filter=${encodeURIComponent(filter)}` : '';
  const response = await fetch(`${API_URL}/api/reports/${id}${qs}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Failed to load report ${id}: ${response.status}`);
  }
  return response.json();
}

export async function getClaim(id: string, claimId: string): Promise<Claim> {
  const response = await fetch(`${API_URL}/api/reports/${id}/claims/${claimId}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Failed to load claim ${claimId}: ${response.status}`);
  }
  return response.json();
}

/** Download the report as a native, editable PowerPoint deck. Fetches with the
 * session cookie, then saves the returned .pptx using its server filename. */
export async function downloadReportPptx(id: string): Promise<void> {
  const response = await fetch(`${API_URL}/api/reports/${id}/export.pptx`, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Export failed: ${response.status}`);
  }
  const blob = await response.blob();
  const filename = filenameFromContentDisposition(
    response.headers.get('Content-Disposition'),
    `report-${id}.pptx`,
  );
  downloadBlob(blob, filename);
}

// ── Report chat (claims-first, with graceful decline) ────────────────────────

/** Response to an /ask call. Answers come strictly from the report's claims:
 *  'claims'  → an inline, cited answer.
 *  'decline' → the claims can't answer it (out of scope, or it needs raw
 *              respondent data that lives in the standalone Survey Chat tool). */
export interface ReportChatAskResponse {
  mode: 'claims' | 'decline';
  answer?: string;
  citedClaimIds?: string[];
  followUps?: string[];
  // decline:
  declineReason?: string;
  adjacent?: string[];
  reason?: string;
}

/** Ask a question of a report. Answers are drawn from the report's own claims and
 * cited; anything the claims can't answer is declined gracefully. */
export async function askReport(
  reportId: string,
  query: string,
  scopeClaimId?: string,
): Promise<ReportChatAskResponse> {
  const response = await fetch(`${API_URL}/report-chat/${reportId}/ask`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, scope_claim_id: scopeClaimId ?? null }),
  });
  if (!response.ok) {
    throw new Error(`Chat request failed: ${response.status}`);
  }
  return response.json();
}

/** Load the persisted conversation for a report (one thread per user+report),
 * so the panel restores history on reload. Returns the raw message list. */
export async function getReportChatHistory(
  reportId: string,
): Promise<Array<Record<string, unknown>>> {
  const response = await fetch(`${API_URL}/report-chat/${reportId}/history`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) return [];
  const data = await response.json();
  return Array.isArray(data.messages) ? data.messages : [];
}
